import type { LinkItemsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { LinkItemsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface LinkItemsInput {
  Source: string;
  Targets: string[];
}

export const useLinkItems = createMutationHook({
  name: 'linkItems',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.linkedItem.link.mutationOptions({ onSuccess }),
  mapInput: (input: LinkItemsInput) => input,
  invalidate: (trpc) => [
    // COI-889: router-level key — `linkedItemRisks` and
    // `linkedRisksByInternalAuditId` were reached by nothing.
    trpc.frontend.linkedItem.pathKey(),
    // COI-471 parity: Apollo twin also evicts every linkable entity family
    // (linking changes each side's linked-items tab / counts).
    trpc.frontend.acceptance.pathKey(),
    trpc.frontend.action.pathKey(),
    trpc.frontend.appetite.pathKey(),
    trpc.frontend.control.pathKey(),
    trpc.frontend.controlGroup.pathKey(),
    trpc.frontend.document.pathKey(),
    trpc.frontend.indicator.pathKey(),
    trpc.frontend.internalAuditEntity.pathKey(),
    trpc.frontend.issue.pathKey(),
    trpc.frontend.obligation.pathKey(),
    trpc.frontend.obligationChange.pathKey(),
    trpc.frontend.risk.pathKey(),
    // COI-889: linking inserts entity `_parent` rows (linked-item-repository),
    // whose forward triggers maintain the `node_ancestor` table behind
    // `ancestor_contributor_view` — the ribbon's inherited-ownership filters.
    trpc.frontend.myItems.pathKey(),
  ],
  // Consumers ignore the payload — surface an empty link set.
  mapResponse: (): LinkItemsMutation => ({
    linkItems: {
      __typename: 'LinkItemsOutput',
      Links: [],
    },
  }),
  graphqlDocument: LinkItemsDocument,
  graphqlVariables: (input: LinkItemsInput) => ({
    Source: input.Source,
    Targets: input.Targets,
  }),
  evictFields: [
    'linked_item',
    'control',
    'action',
    'issue',
    'internal_audit_entity',
    'internal_audit_entity_aggregate',
    'appetite',
    'risk',
  ],
});
